import type { RGB } from '@rapoo/device-sdk'

import { rgbToHex } from '@/utils/color'

export const SCREEN_DISPLAY_WIDTH = 214
export const SCREEN_DISPLAY_HEIGHT = 135
export const PREVIEW_BASE_HEIGHT_PX = 38 * 4

export type ScreenTextSize = 'small' | 'medium' | 'large'

export interface ScreenTextStyle {
  content: string
  textColor: RGB
  bgColor: RGB
  size: ScreenTextSize
}

export const SCREEN_TEXT_FONT_SIZES: Record<ScreenTextSize, number> = {
  small: 20,
  medium: 40,
  large: 58,
}

export const SCREEN_TEXT_CHAR_LIMITS: Record<ScreenTextSize, number> = {
  small: 44,
  medium: 15,
  large: 8,
}

export function createDefaultScreenTextStyle(): ScreenTextStyle {
  return {
    content: '',
    textColor: { r: 255, g: 255, b: 255 },
    bgColor: { r: 0, g: 0, b: 0 },
    size: 'small',
  }
}

export function normalizeScreenTextContent(content: string) {
  return content.replace(/\s+/g, ' ').trim()
}

export function getPreviewFontSizeCqh(size: ScreenTextSize) {
  return (SCREEN_TEXT_FONT_SIZES[size] / PREVIEW_BASE_HEIGHT_PX) * 100
}

export function getScreenTextFontSize(size: ScreenTextSize, height = SCREEN_DISPLAY_HEIGHT) {
  return (SCREEN_TEXT_FONT_SIZES[size] / PREVIEW_BASE_HEIGHT_PX) * height
}

function truncateCanvasText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  if (ctx.measureText(text).width <= maxWidth) return text

  const ellipsis = '...'
  let truncated = text

  while (truncated.length > 0 && ctx.measureText(truncated + ellipsis).width > maxWidth) {
    truncated = truncated.slice(0, -1)
  }

  return truncated ? truncated + ellipsis : ellipsis
}

function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Failed to export canvas'))
    }, 'image/png')
  })
}

/** 将文字样式渲染为屏显尺寸 PNG */
export async function renderScreenTextToBlob(style: ScreenTextStyle) {
  const canvas = document.createElement('canvas')
  canvas.width = SCREEN_DISPLAY_WIDTH
  canvas.height = SCREEN_DISPLAY_HEIGHT

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  const { r, g, b } = style.bgColor
  ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const text = normalizeScreenTextContent(style.content)
  if (!text) return canvasToBlob(canvas)

  const fontSize = getScreenTextFontSize(style.size)
  ctx.font = `${fontSize}px OPPOSans, sans-serif`
  ctx.fillStyle = rgbToHex(style.textColor)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const displayText = truncateCanvasText(ctx, text, SCREEN_DISPLAY_WIDTH)

  ctx.fillText(displayText, SCREEN_DISPLAY_WIDTH / 2, SCREEN_DISPLAY_HEIGHT / 2)

  return canvasToBlob(canvas)
}
